import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, Check, Trash2, Plus, Loader2 } from 'lucide-react';
import { Eyebrow, RaceBib } from '@/components/foundry';
import { useAppAuth } from '@/lib/appAuth';
import { runConvexQuery, runConvexMutation, runConvexAction } from '@/lib/convex';

// ────────────────────────────────────────────────────────────────────────────
// MCP API Keys — Settings section.
//
// Lets the Builder generate / view / revoke personal access tokens that
// authenticate repo-side push to AI Brand Lab (Command Centre receipts feed).
//
// Storage model: SHA-256 hash + 8-char prefix in `mcp_api_keys` table.
// Plaintext key is shown ONCE at creation time -- never stored. Builder
// pastes it into their repo's `.env.local` as `AIBL_API_KEY`.
//
// Endpoint the keys authenticate to:
//   POST /api/action-receipts/push  (convex/http.ts)
// ────────────────────────────────────────────────────────────────────────────

interface ApiKeyRow {
  id: string;
  key_prefix: string;
  label: string;
  project_id: string | null;
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

interface NewKeyResult {
  key_id: string;
  plaintext: string;
  key_prefix: string;
  label: string;
  created_at: string;
}

export default function McpApiKeysSection() {
  const { isLoaded, getToken } = useAppAuth();
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  // New key creation state
  const [creating, setCreating] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [revealedKey, setRevealedKey] = useState<NewKeyResult | null>(null);
  const [copied, setCopied] = useState(false);

  // Revoke state (per-key)
  const [revoking, setRevoking] = useState<string | null>(null);

  const loadKeys = async () => {
    try {
      setState('loading');
      const token = (await getToken?.({ skipCache: true })) ?? null;
      const rows = await runConvexQuery<ApiKeyRow[]>('mcpApiKeys:listMine', {}, token);
      setKeys(rows ?? []);
      setState('ready');
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't load your keys.");
      setState('error');
    }
  };

  useEffect(() => {
    if (!isLoaded) return;
    loadKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  async function handleCreate() {
    if (creating) return;
    const label = newKeyLabel.trim();
    if (label.length === 0) {
      setCreateError('Add a label so you can identify this key later.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const token = (await getToken?.({ skipCache: true })) ?? null;
      const result = await runConvexAction<NewKeyResult>(
        'mcpApiKeys:create',
        { label },
        token,
      );
      setRevealedKey(result);
      setNewKeyLabel('');
      await loadKeys();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "We couldn't create the key.");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(keyId: string, label: string) {
    if (revoking) return;
    const ok = window.confirm(
      `Revoke key "${label}"? Any repo using this key for receipt pushes will stop working immediately. You can generate a fresh one right after.`,
    );
    if (!ok) return;
    setRevoking(keyId);
    try {
      const token = (await getToken?.({ skipCache: true })) ?? null;
      await runConvexMutation('mcpApiKeys:revoke', { key_id: keyId }, token);
      await loadKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't revoke the key.");
    } finally {
      setRevoking(null);
    }
  }

  async function handleCopy(plaintext: string) {
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked; Builder can select manually
    }
  }

  return (
    <section className="rounded-[14px] border border-fd-border bg-fd-paper p-6">
      <div className="flex items-start gap-3">
        <RaceBib n="K" tone="teal" size="sm" shadow="sm" />
        <div className="min-w-0 flex-1">
          <Eyebrow className="block">API Keys</Eyebrow>
          <h3 className="mt-1.5 font-geist text-[16px] font-medium text-fd-text">
            Connect your repo to AI Brand Lab
          </h3>
          <p className="mt-1.5 max-w-[600px] text-[13px] leading-[1.55] text-fd-text-muted">
            Generate a personal access token so your AI Cofounder running in your own repo
            can push action receipts to your Command Centre. Paste the token into your
            repo's <code className="rounded-[3px] border border-fd-border bg-fd-surface-1 px-1 py-0.5 font-geist-mono text-[12px]">.env.local</code> as <code className="rounded-[3px] border border-fd-border bg-fd-surface-1 px-1 py-0.5 font-geist-mono text-[12px]">AIBL_API_KEY</code>. Keys are shown
            once at creation and stored only as a hash here.
          </p>
        </div>
      </div>

      {/* Reveal panel — shown ONCE after a key is created. Sticky, dismissable. */}
      {revealedKey ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="mt-5 rounded-[10px] border-[1.5px] border-fd-accent bg-fd-accent/10 p-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-geist-mono text-[10.5px] font-bold uppercase tracking-[0.06em] text-fd-text">
                New key created — copy it now
              </p>
              <p className="mt-1.5 text-[13px] leading-[1.5] text-fd-text-muted">
                This is the only time we'll show the full key. Paste it into your
                repo's <code className="rounded-[3px] border border-fd-border bg-fd-paper/60 px-1 py-0.5 font-geist-mono text-[12px]">.env.local</code> right now:
              </p>
            </div>
          </div>
          <pre className="mt-3 select-all overflow-x-auto rounded-[6px] border border-fd-border bg-fd-paper px-3 py-2.5 font-geist-mono text-[12.5px] text-fd-text">
{`AIBL_API_BASE_URL=${convexSiteUrl()}
AIBL_API_KEY=${revealedKey.plaintext}`}
          </pre>
          <div className="mt-3 flex flex-wrap items-center gap-2.5">
            <button
              type="button"
              onClick={() => handleCopy(`AIBL_API_BASE_URL=${convexSiteUrl()}\nAIBL_API_KEY=${revealedKey.plaintext}`)}
              className="inline-flex h-8 items-center gap-1.5 rounded-[5px] border-[1.5px] border-fd-text bg-fd-paper px-3 font-geist-mono text-[11.5px] font-bold uppercase tracking-[0.04em] text-fd-text transition-colors hover:bg-fd-accent"
            >
              {copied ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : <Copy className="h-3.5 w-3.5" strokeWidth={2.25} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              onClick={() => setRevealedKey(null)}
              className="inline-flex h-8 items-center rounded-[5px] border border-fd-border bg-fd-paper px-3 font-geist-mono text-[11.5px] font-medium uppercase tracking-[0.04em] text-fd-text-muted transition-colors hover:text-fd-text"
            >
              I've saved it
            </button>
          </div>
        </motion.div>
      ) : null}

      {/* Create form */}
      <div className="mt-6 grid gap-2.5">
        <label htmlFor="new-key-label" className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-muted">
          New key label
        </label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
          <input
            id="new-key-label"
            type="text"
            value={newKeyLabel}
            onChange={(e) => setNewKeyLabel(e.target.value)}
            placeholder="e.g. MacBook Pro"
            maxLength={80}
            disabled={creating}
            className="flex-1 rounded-[6px] border border-fd-border bg-fd-paper px-3 py-2 font-geist text-[14px] text-fd-text placeholder:text-fd-text-subtle focus:border-fd-text focus:outline-none focus:ring-1 focus:ring-fd-text disabled:opacity-60"
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating || newKeyLabel.trim().length === 0}
            className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-[5px] border-[1.5px] border-fd-text bg-fd-paper px-4 font-geist-mono text-[12px] font-bold uppercase tracking-[0.04em] text-fd-text transition-colors hover:bg-fd-accent hover:text-[oklch(0.16_0.005_95)] disabled:opacity-50"
          >
            {creating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.25} />
            ) : (
              <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
            )}
            {creating ? 'Creating' : 'Create key'}
          </button>
        </div>
        {createError ? (
          <p role="alert" className="rounded-[5px] border border-fd-coral/40 bg-fd-coral-soft px-3 py-1.5 font-geist text-[12px] text-fd-coral">
            {createError}
          </p>
        ) : null}
      </div>

      {/* Existing keys list */}
      <div className="mt-7">
        <p className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-muted">
          Your keys
        </p>
        {state === 'loading' ? (
          <div className="mt-3 space-y-2">
            <div className="h-12 rounded-[6px] bg-fd-surface-1 animate-pulse" />
            <div className="h-12 rounded-[6px] bg-fd-surface-1 animate-pulse" />
          </div>
        ) : state === 'error' ? (
          <p role="alert" className="mt-3 rounded-[5px] border border-fd-coral/40 bg-fd-coral-soft px-3 py-2 font-geist text-[12px] text-fd-coral">
            {error}
          </p>
        ) : keys.length === 0 ? (
          <p className="mt-3 rounded-[6px] border border-dashed border-fd-border bg-fd-surface-1/40 px-4 py-4 font-geist text-[13px] text-fd-text-muted">
            No keys yet. Create one above to start pushing receipts from your repo.
          </p>
        ) : (
          <ul className="mt-3 grid gap-2">
            {keys.map((k) => (
              <KeyRow key={k.id} k={k} onRevoke={handleRevoke} revoking={revoking === k.id} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function KeyRow({ k, onRevoke, revoking }: { k: ApiKeyRow; onRevoke: (id: string, label: string) => void; revoking: boolean }) {
  const isRevoked = !!k.revoked_at;
  return (
    <li
      className={`flex flex-wrap items-center justify-between gap-3 rounded-[6px] border border-fd-border bg-fd-paper px-3 py-2.5 ${isRevoked ? 'opacity-60' : ''}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="font-geist text-[14px] font-medium text-fd-text">{k.label}</span>
          <code className="font-geist-mono text-[11.5px] text-fd-text-subtle">
            {k.key_prefix}…
          </code>
          {isRevoked ? (
            <span className="rounded-[3px] border border-fd-coral/40 bg-fd-coral-soft px-1.5 py-0.5 font-geist-mono text-[9.5px] font-semibold uppercase tracking-[0.06em] text-fd-coral">
              Revoked
            </span>
          ) : null}
        </div>
        <p className="mt-0.5 font-geist-mono text-[10.5px] text-fd-text-subtle">
          Created {formatRelativeDate(k.created_at)}
          {k.last_used_at ? ` · Last used ${formatRelativeDate(k.last_used_at)}` : ' · Not used yet'}
        </p>
      </div>
      {!isRevoked ? (
        <button
          type="button"
          onClick={() => onRevoke(k.id, k.label)}
          disabled={revoking}
          aria-label={`Revoke key ${k.label}`}
          className="inline-flex h-8 items-center gap-1.5 rounded-[5px] border border-fd-border bg-fd-paper px-3 font-geist-mono text-[11px] font-medium uppercase tracking-[0.04em] text-fd-text-muted transition-colors hover:border-fd-coral hover:text-fd-coral disabled:opacity-50"
        >
          {revoking ? (
            <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.25} />
          ) : (
            <Trash2 className="h-3 w-3" strokeWidth={2.25} />
          )}
          {revoking ? 'Revoking' : 'Revoke'}
        </button>
      ) : null}
    </li>
  );
}

// Resolve the Convex `.convex.site` URL where the AI Brand Lab HTTP routes live.
// VITE_CONVEX_SITE_URL is set in .env.local alongside VITE_CONVEX_URL. Falls back
// to deriving from VITE_CONVEX_URL (`.convex.cloud` -> `.convex.site`) so this
// works even if the explicit site URL isn't set.
function convexSiteUrl(): string {
  const explicit = import.meta.env.VITE_CONVEX_SITE_URL as string | undefined;
  if (explicit) return explicit;
  const cloudUrl = import.meta.env.VITE_CONVEX_URL as string | undefined;
  if (cloudUrl) {
    return cloudUrl.replace(/\.convex\.cloud(\/?$)/, '.convex.site$1');
  }
  return 'https://<your-aibl-deployment>.convex.site';
}

function formatRelativeDate(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diffMs = Date.now() - t;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
